// src/index.ts
function slugify(text) {
  return text.toLowerCase().trim().replace(/[^\w\s-]/g, "").replace(/[\s_-]+/g, "-").replace(/^-+|-+$/g, "");
}
function generateCompetitionCode(name, year) {
  const y = year ?? (/* @__PURE__ */ new Date()).getFullYear();
  const parts = name.toUpperCase().replace(/[^A-Z0-9\s]/g, "").split(/\s+/).filter(Boolean).slice(0, 3).map((w) => w.slice(0, 4));
  return `${parts.join("-")}-${y}`;
}
function formatScoreCm(cm, options) {
  const opts = typeof options === "number" ? { maximum: options, width: 3 } : { maximum: options?.maximum, width: options?.width ?? 3 };
  if (cm === null || cm === void 0 || Number.isNaN(Number(cm))) return "\u2014";
  let value = Number(cm);
  if (opts.maximum != null && value >= opts.maximum) {
    value = opts.maximum;
  }
  const width = opts.width ?? 3;
  if (Number.isInteger(value)) {
    return String(value).padStart(width, "0");
  }
  const [intPart, frac] = value.toFixed(1).split(".");
  return `${intPart.padStart(width, "0")}.${frac}`;
}
var formatScore = formatScoreCm;
function formatPilotName(firstName, lastName) {
  return `${firstName} ${lastName}`.trim();
}
function padPilotNumber(n, width = 3) {
  return String(n).padStart(width, "0");
}
function shuffleArray(items, seed) {
  const arr = [...items];
  let s = seed ?? Date.now();
  const random = () => {
    s = (s * 1664525 + 1013904223) % 4294967296;
    return s / 4294967296;
  };
  for (let i = arr.length - 1; i > 0; i--) {
    const j = Math.floor(random() * (i + 1));
    [arr[i], arr[j]] = [arr[j], arr[i]];
  }
  return arr;
}
function generateQrPayload(publicResultsUrl, competitionSlug, path = "") {
  const base = publicResultsUrl.replace(/\/$/, "");
  return `${base}/${competitionSlug}${path}`;
}
function sleep(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}
function groupBy(items, keyFn) {
  return items.reduce(
    (acc, item) => {
      const key = keyFn(item);
      (acc[key] ??= []).push(item);
      return acc;
    },
    {}
  );
}
function clamp(value, min, max) {
  return Math.min(max, Math.max(min, value));
}
function parseCsvLine(line) {
  const result = [];
  let current = "";
  let inQuotes = false;
  for (let i = 0; i < line.length; i++) {
    const ch = line[i];
    if (ch === '"') {
      if (inQuotes && line[i + 1] === '"') {
        current += '"';
        i++;
      } else {
        inQuotes = !inQuotes;
      }
    } else if (ch === "," && !inQuotes) {
      result.push(current.trim());
      current = "";
    } else {
      current += ch;
    }
  }
  result.push(current.trim());
  return result;
}
function toCsv(rows) {
  return rows.map(
    (row) => row.map((cell) => {
      const needsQuotes = /[",\n]/.test(cell);
      const escaped = cell.replace(/"/g, '""');
      return needsQuotes ? `"${escaped}"` : escaped;
    }).join(",")
  ).join("\n");
}
function isWindReadingCurrent(recordedAt, maxAgeMinutes, now = Date.now()) {
  if (!Number.isFinite(maxAgeMinutes) || maxAgeMinutes <= 0) return true;
  if (recordedAt == null || recordedAt === "") return true;
  const at = recordedAt instanceof Date ? recordedAt.getTime() : new Date(recordedAt).getTime();
  if (Number.isNaN(at)) return false;
  return now - at <= maxAgeMinutes * 6e4;
}
function ageFromDob(dob, reference = /* @__PURE__ */ new Date()) {
  let age = reference.getFullYear() - dob.getFullYear();
  const m = reference.getMonth() - dob.getMonth();
  if (m < 0 || m === 0 && reference.getDate() < dob.getDate()) age--;
  return age;
}
var STAFF_SESSION_HANDOFF_PREFIX = "aj_staff_handoff=";
function toBase64Url(value) {
  const bytes = new TextEncoder().encode(value);
  let binary = "";
  for (const byte of bytes) binary += String.fromCharCode(byte);
  return btoa(binary).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}
function fromBase64Url(value) {
  const padded = value.replace(/-/g, "+").replace(/_/g, "/");
  const padLength = (4 - padded.length % 4) % 4;
  const binary = atob(padded + "=".repeat(padLength));
  const bytes = Uint8Array.from(binary, (c) => c.charCodeAt(0));
  return new TextDecoder().decode(bytes);
}
function encodeStaffSessionHandoff(payload) {
  return `${STAFF_SESSION_HANDOFF_PREFIX}${toBase64Url(JSON.stringify(payload))}`;
}
function displayCountryCode(country, nationality) {
  const code = country?.code?.trim().toUpperCase() ?? "";
  if (/^[A-Z]{3}$/.test(code)) return code;
  const stored = nationality?.trim().toUpperCase() ?? "";
  return /^[A-Z]{3}$/.test(stored) ? stored : "";
}
function isOfficialRound(round) {
  return (round.type ?? "OFFICIAL") === "OFFICIAL";
}
function officialDisplayNumber(rounds, storedNumber) {
  const official = rounds.filter(isOfficialRound).sort((a, b) => a.number - b.number);
  const index = official.findIndex((round) => round.number === storedNumber);
  return index >= 0 ? index + 1 : null;
}
function nextOfficialStoredNumber(rounds) {
  return rounds.filter(isOfficialRound).reduce((max, round) => Math.max(max, round.number), 0) + 1;
}
function nextOfficialDisplayNumber(rounds) {
  return rounds.filter(isOfficialRound).length + 1;
}
function announcedRoundLabel(round, rounds) {
  const name = round.name?.trim() ?? "";
  const genericNumber = !name || /^round \d+$/i.test(name);
  if (round.type === "PRACTICE") {
    return genericNumber ? "Practice round" : name;
  }
  if (round.type && round.type !== "OFFICIAL") {
    const kind = round.type.charAt(0) + round.type.slice(1).toLowerCase();
    return genericNumber ? kind : `${kind} \u2014 ${name}`;
  }
  const display = officialDisplayNumber(rounds, round.number) ?? round.number;
  const generic = genericNumber || name.toLowerCase() === `round ${round.number}` || name.toLowerCase() === `round ${display}`;
  return generic ? `Round ${display}` : name;
}
var FINISHED_FLIGHT_STATUSES = /* @__PURE__ */ new Set(["SCORED", "DNF", "ABS", "DNS", "DSQ"]);
var RECORDED_SCORE_STATUSES = /* @__PURE__ */ new Set(["ENTERED", "CONFIRMED", "DISPUTED", "APPROVED", "LOCKED"]);
function flightStillToFly(input) {
  if (input.status === "REFLIGHT" || input.score?.resultType === "REFLIGHT") return true;
  if (FINISHED_FLIGHT_STATUSES.has(input.status)) return false;
  const score = input.score;
  if (score && score.finalScoreCm != null && score.resultType !== "REFLIGHT" && (score.status == null || RECORDED_SCORE_STATUSES.has(score.status))) {
    return false;
  }
  return true;
}
function onDeckSlots(flightsInOrder, anchorPilotId) {
  const anchorIndex = anchorPilotId == null ? -1 : flightsInOrder.findIndex((flight) => flight.pilotId === anchorPilotId);
  const rotated = anchorIndex < 0 ? flightsInOrder : [...flightsInOrder.slice(anchorIndex + 1), ...flightsInOrder.slice(0, anchorIndex)];
  const waiting = rotated.filter(
    (flight) => flight.stillToFly && flight.pilotId !== anchorPilotId
  );
  const anchor = anchorIndex >= 0 ? flightsInOrder[anchorIndex] : void 0;
  if (anchor?.stillToFly) {
    return { nowFlying: anchor, queue: waiting };
  }
  const [nowFlying, ...queue] = waiting;
  if (nowFlying) return { nowFlying, queue };
  return { nowFlying: anchor ?? null, queue: [] };
}
function parseStaffSessionHandoff(hash) {
  const raw = hash.startsWith("#") ? hash.slice(1) : hash;
  if (!raw.startsWith(STAFF_SESSION_HANDOFF_PREFIX)) return null;
  try {
    const encoded = raw.slice(STAFF_SESSION_HANDOFF_PREFIX.length);
    const parsed = JSON.parse(fromBase64Url(encoded));
    if (!parsed?.accessToken || typeof parsed.accessToken !== "string") return null;
    return parsed;
  } catch {
    return null;
  }
}
export {
  STAFF_SESSION_HANDOFF_PREFIX,
  ageFromDob,
  announcedRoundLabel,
  clamp,
  displayCountryCode,
  encodeStaffSessionHandoff,
  flightStillToFly,
  formatPilotName,
  formatScore,
  formatScoreCm,
  generateCompetitionCode,
  generateQrPayload,
  groupBy,
  isOfficialRound,
  isWindReadingCurrent,
  nextOfficialDisplayNumber,
  nextOfficialStoredNumber,
  officialDisplayNumber,
  onDeckSlots,
  padPilotNumber,
  parseCsvLine,
  parseStaffSessionHandoff,
  shuffleArray,
  sleep,
  slugify,
  toCsv
};
